'use client'

import { useId, useMemo } from 'react'
import { InstrumentLabel } from './ui'
import { Chip } from './ui/GradeChip'
import { useTrendHistory, type TrendPoint } from './useTrendHistory'

/**
 * Compact score-over-time chart for the dashboard hero.
 *
 * Hand-rolled SVG, no chart library: the shape is a single series with no
 * axes to configure and no interaction, so Recharts would cost a client
 * bundle for nothing. This is now the only trend on the dashboard — the
 * History tab's duplicate chart and its TrendChart component were removed
 * 2026-07-26, so one series is rendered in one place.
 *
 * Renders a caption instead of a line when there are fewer than two runs —
 * one point is not a trend, and drawing a flat line from it implies stability
 * that was never measured.
 */

const VB_H = 170
const PAD_L = 20
const PAD_R = 22
const TOP = 30
const BOTTOM = 150

export function TrendSparkline({
  domain,
  data: external,
  weeks = 8,
  viewWidth = 560,
}: {
  domain: string | null | undefined
  data?: TrendPoint[]
  weeks?: number
  /**
   * viewBox width. The SVG scales to its container, so this is really the
   * aspect ratio — and therefore the rendered HEIGHT. 560 suits the hero's
   * right column; a full-width band needs a wider box or the chart grows to
   * a third of the viewport. Height is fixed at 170 either way.
   */
  viewWidth?: number
}) {
  const VB_W = viewWidth
  const gradientId = useId()
  const { data, loading, error } = useTrendHistory(domain, external)

  const series = useMemo(() => data.slice(-weeks), [data, weeks])

  const geometry = useMemo(() => {
    if (series.length < 2) return null
    const values = series.map(d => d.overallScore)
    const rawMin = Math.min(...values)
    const rawMax = Math.max(...values)
    // Pad the domain so a flat-ish series doesn't render as a line glued to
    // an edge, then snap to tens so the axis reads 70 / 50 / 30 rather than
    // 70 / 51 / 31 — matching the reference's clean scale.
    // Guard the zero-range case explicitly: (v-min)/(max-min) would be NaN
    // when every score is identical.
    const min = Math.max(0, Math.floor((rawMin - 10) / 10) * 10)
    const max = Math.min(100, Math.ceil((rawMax + 10) / 10) * 10)
    const range = max - min || 1

    const stepX =
      series.length > 1 ? (VB_W - PAD_L - PAD_R) / (series.length - 1) : 0
    const points = series.map((d, i) => ({
      x: PAD_L + i * stepX,
      y: BOTTOM - ((d.overallScore - min) / range) * (BOTTOM - TOP),
      score: d.overallScore,
      checkedAt: d.checkedAt,
    }))

    const line = points
      .map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ')
    const area = `${line} ${points[points.length - 1].x.toFixed(1)},${BOTTOM} ${PAD_L},${BOTTOM}`

    const first = values[0]
    const last = values[values.length - 1]

    return {
      points,
      line,
      area,
      min,
      max,
      mid: Math.round((min + max) / 2),
      allTimeDelta: last - first,
      last: points[points.length - 1],
    }
  }, [series, VB_W])

  if (loading) {
    return (
      <div className='flex min-h-[180px] items-center px-8 py-7'>
        <span className='text-xs text-instrument'>Loading trend…</span>
      </div>
    )
  }

  if (!geometry) {
    return (
      <div className='flex min-h-[180px] flex-col justify-center gap-2 px-8 py-7'>
        <InstrumentLabel tone='secondary'>Score trend</InstrumentLabel>
        <span className='block text-sm text-muted-foreground'>
          {/* An failed request says nothing about how many audits exist, so
              it must NOT be reported as "no history" — that is a claim about
              the customer's data derived from a measurement that never
              completed. */}
          {error
            ? 'Trend history could not be loaded. Refresh to try again.'
            : series.length === 0
              ? 'No history yet. Your trend appears after the second audit of this domain.'
              : 'One audit recorded. Run another to start the trend.'}
        </span>
      </div>
    )
  }

  const { allTimeDelta, last } = geometry
  const deltaColor =
    allTimeDelta > 0
      ? 'var(--growth)'
      : allTimeDelta < 0
        ? 'var(--cta)'
        : 'var(--pm-text-muted)'

  return (
    <div className='flex flex-1 flex-col gap-4 px-8 pb-5 pt-7'>
      <div className='flex flex-wrap items-center gap-3'>
        <InstrumentLabel tone='secondary'>
          Score trend · {series.length} run{series.length === 1 ? '' : 's'}
        </InstrumentLabel>
        <Chip color={deltaColor}>
          {allTimeDelta > 0 ? '+' : ''}
          {allTimeDelta} all time
        </Chip>
      </div>

      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className='block h-auto w-full'
        role='img'
        aria-label={`Score trend across ${series.length} audits, from ${geometry.points[0].score} to ${last.score} out of 100.`}
      >
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0' stopColor='#008dcc' stopOpacity='0.22' />
            <stop offset='1' stopColor='#008dcc' stopOpacity='0' />
          </linearGradient>
        </defs>

        {[TOP, 60, 90, 120].map(y => (
          <line
            key={y}
            x1={PAD_L}
            y1={y}
            x2={VB_W - PAD_R}
            y2={y}
            stroke='rgba(255,255,255,0.07)'
            strokeWidth='1'
          />
        ))}
        <line
          x1={PAD_L}
          y1={BOTTOM}
          x2={VB_W - PAD_R}
          y2={BOTTOM}
          stroke='rgba(255,255,255,0.14)'
          strokeWidth='1'
        />

        <text
          x={VB_W - PAD_R + 8}
          y={TOP + 3}
          fontSize='10'
          fill='#71717a'
          fontFamily='var(--font-geist-mono)'
        >
          {geometry.max}
        </text>
        <text
          x={VB_W - PAD_R + 8}
          y={93}
          fontSize='10'
          fill='#71717a'
          fontFamily='var(--font-geist-mono)'
        >
          {geometry.mid}
        </text>
        <text
          x={VB_W - PAD_R + 8}
          y={BOTTOM + 3}
          fontSize='10'
          fill='#71717a'
          fontFamily='var(--font-geist-mono)'
        >
          {geometry.min}
        </text>

        <polygon points={geometry.area} fill={`url(#${gradientId})`} />
        <polyline
          points={geometry.line}
          fill='none'
          stroke='#008dcc'
          strokeWidth='2'
          strokeLinejoin='round'
          strokeLinecap='round'
        />
        {geometry.points.slice(0, -1).map(p => (
          <circle key={p.checkedAt} cx={p.x} cy={p.y} r='3' fill='#008dcc' />
        ))}
        <circle cx={last.x} cy={last.y} r='4.5' fill='#fdc304' />
        <text
          x={Math.min(last.x, VB_W - PAD_R - 10)}
          y={Math.max(last.y - 14, 14)}
          fontSize='12'
          fill='#fdc304'
          textAnchor='end'
          fontFamily='var(--font-geist-mono)'
        >
          {last.score}
        </text>
      </svg>
    </div>
  )
}
